import type { AxiosInstance, AxiosResponse } from 'axios'

import type { CamelCased } from '../types/requests.types'
import { transformKeysToCamelCase } from '../utils/transform-keys-to-camel-case'

/**
 * Transforms a function's return type from Promise<AxiosResponse<T>> to Promise<CamelCased<T>>.
 */
type CamelCaseMethod<F> = F extends (...args: infer A) => Promise<AxiosResponse<infer R>>
  ? (...args: A) => Promise<CamelCased<R>>
  : F

/**
 * Maps every method in a client object to return CamelCased data.
 */
export type CamelCaseClient<T> = {
  [K in keyof T]: CamelCaseMethod<T[K]>
}

/**
 * Wraps an orval-generated client factory so that every method:
 * - Unwraps `AxiosResponse` (returns `.data` directly)
 * - Transforms response data keys to camelCase via `transformKeysToCamelCase`
 * - Is typed as `CamelCased<T>` to reflect the transformed shape
 *
 * @example
 * ```ts
 * const client = createCamelCasedClient(getContentLibrary, axiosInstance)
 * const data = await client.listContentItems() // CamelCased, unwrapped
 * ```
 */
export function createCamelCasedClient<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends Record<string, (...args: any[]) => Promise<AxiosResponse>>,
>(clientFactory: (axios: AxiosInstance) => T, axiosInstance: AxiosInstance): CamelCaseClient<T> {
  const rawClient = clientFactory(axiosInstance)
  const wrappedClient = {} as Record<string, (...args: unknown[]) => Promise<unknown>>

  for (const key of Object.keys(rawClient)) {
    wrappedClient[key] = (...args: unknown[]) =>
      (rawClient[key] as (...a: unknown[]) => Promise<AxiosResponse>)(...args).then((res) =>
        transformKeysToCamelCase(res.data),
      )
  }

  return wrappedClient as CamelCaseClient<T>
}
